import { Injectable } from '@angular/core';
import mixpanel from 'mixpanel-browser';
import { environment } from '../../../environments/environment';

/**
 * Thin, security-hardened wrapper around mixpanel-browser.
 *
 * Platform tagging
 * ----------------
 * The user app and the partner dashboard send to the SAME Mixpanel project. To
 * keep their data segmentable without opening each profile, every event carries a
 * `platform` super-property and every identified profile carries a `platform`
 * people-property. In Mixpanel you can then filter the Users list and any report
 * by `platform = user` / `platform = partner`. This repo is the PARTNER dashboard.
 *
 * Security model
 * --------------
 * - The project token shipped in the bundle is WRITE-ONLY (event ingestion). It
 *   cannot read any analytics data — reading requires the Mixpanel API Secret /
 *   a Service Account, which never leaves the server. So our analytics data is
 *   only visible to Mixpanel and to authenticated members of our Mixpanel project.
 * - sanitize() is a defensive guard so no secret/credential can ever leave the
 *   browser through an event property, even if a future track() call passes one in.
 * - secure_cookie keeps the Mixpanel identity cookie HTTPS-only.
 */
@Injectable({ providedIn: 'root' })
export class MixpanelService {
    private ready = false;

    // Which frontend this is. Stamped on every event + profile for segmentation.
    private static readonly PLATFORM = 'partner';

    // Property names that must never be forwarded to Mixpanel. Matched as
    // substrings against a normalised (lower-case, alpha-only) version of the key.
    private static readonly BLOCKED_KEY_PARTS = [
        'password', 'passwd', 'secret', 'apikey', 'accesskey', 'token',
        'authorization', 'privatekey', 'mnemonic', 'seedphrase', 'csrf',
        'cookie', 'walletaddress', 'cardnumber', 'cvv',
    ];

    init(): void {
        if (!environment.mixpanelToken) return;
        mixpanel.init(environment.mixpanelToken, {
            track_pageview: false,
            persistence: 'localStorage',
            secure_cookie: true,
        });
        // Tag every event with the platform so reports can be split user vs partner.
        mixpanel.register({ platform: MixpanelService.PLATFORM });
        this.ready = true;
    }

    identify(userId: string, traits?: Record<string, unknown>): void {
        if (!this.ready) return;
        mixpanel.identify(userId);
        // Always tag the profile with platform so the Users list is filterable
        // by user-app vs partner-dashboard without opening each profile.
        mixpanel.people.set({
            platform: MixpanelService.PLATFORM,
            ...this.sanitize(traits),
        });
    }

    track(event: string, properties?: Record<string, unknown>): void {
        if (!this.ready) return;
        mixpanel.track(event, this.sanitize(properties));
    }

    reset(): void {
        if (!this.ready) return;
        mixpanel.reset();
    }

    /**
     * Strip any property whose name looks like a credential/secret so it can
     * never reach Mixpanel. Keeps the analytics payload to non-sensitive,
     * behavioural data only.
     */
    private sanitize(
        props?: Record<string, unknown>,
    ): Record<string, unknown> {
        const clean: Record<string, unknown> = {};
        if (!props) return clean;
        for (const [key, value] of Object.entries(props)) {
            const normalised = key.toLowerCase().replace(/[^a-z]/g, '');
            const isSensitive = MixpanelService.BLOCKED_KEY_PARTS.some((part) =>
                normalised.includes(part),
            );
            if (!isSensitive) clean[key] = value;
        }
        return clean;
    }
}
